/**
 * Where this build talks to, and what it is running inside.
 *
 * The client has always derived both from `document.location.hostname`, which works fine for the
 * only two things that existed: a browser tab on projectsamsara.com, and a dev tab on localhost.
 * The native shell breaks that assumption. Capacitor serves the bundled page from
 * `capacitor://localhost` on iOS and `https://localhost` on Android, so a hostname check reads the
 * shipped app as a developer's laptop -- it would dial `ws://localhost:8080` at the phone itself
 * and show the dev name gate to every player. Hence the native check comes FIRST below and the
 * local-dev branch is explicitly gated on it.
 *
 * Everything is overridable through `window.__SAMSARA_CONFIG__`, which the mobile build writes
 * into the bundle. That is how a device build is pointed at a laptop on the LAN for profiling --
 * the thing `API_HOST` was supposed to do and never did, because `process?.env?.API_HOST` is
 * optional-chained and Parcel only substitutes the bare `process.env.X` form. The old expression
 * therefore always fell through to the production default, and a build made with API_HOST set
 * silently talked to production anyway.
 *
 * Reading the environment and deciding from it are kept apart: `resolveConfig` is pure, so the
 * decisions can be tested directly. jsdom will not let a test change `location.hostname` -- it is
 * an unforgeable property -- so a seam is the only way to cover the case this module exists for.
 */

/** Production endpoints, used by the web build and by a release shell alike. */
const PRODUCTION_API_HOST = "wss://api.projectsamsara.com"
const PRODUCTION_ORIGIN = "https://projectsamsara.com"

/** The port `yarn workspace server start` listens on. */
const LOCAL_SERVER_PORT = 8080

export interface RuntimeConfig {
    /** WebSocket endpoint for the game server. */
    apiHost: string
    /**
     * Prefix for relative `assets/...` urls. Empty means "same origin", which is what every
     * browser build wants. The native shell sets it, because its origin is the app bundle and the
     * 200MB+ of art is not in there.
     */
    assetBase: string
    /** Running inside the native shell rather than a browser tab. */
    isNative: boolean
    /** Connect straight as a guest, skipping the local-dev name gate. */
    autoGuest: boolean
}

/** The shape the native shell (or a staging build) may inject. Every field is optional. */
export type InjectedConfig = Partial<RuntimeConfig>

/** Everything `resolveConfig` needs from the outside world. */
export interface ConfigEnvironment {
    hostname: string
    /** Whether Capacitor's bridge is present on `window`. */
    hasNativeBridge: boolean
    injected: InjectedConfig
}

export const readEnvironment = (): ConfigEnvironment => ({
    hostname: typeof document !== "undefined" ? document.location.hostname : "",
    // The bridge is injected at document start, before any page script runs, so this is reliable
    // from the very first line -- including the inline bootstrap in index.html, which asks the
    // same question before the bundle has loaded.
    hasNativeBridge: typeof window !== "undefined" && !!(window as any).Capacitor,
    injected: (typeof window !== "undefined" && (window as any).__SAMSARA_CONFIG__) || {},
})

export const resolveConfig = (env: ConfigEnvironment = readEnvironment()): RuntimeConfig => {
    const { hostname, hasNativeBridge, injected } = env

    const native = injected.isNative ?? hasNativeBridge
    // A dev build in a browser tab: Parcel on localhost, or the LAN address a phone on the same
    // wifi uses to reach it. Deliberately false in the shell, whose origin is also "localhost".
    const localDev = !native && (hostname === "localhost" || hostname.startsWith("10.0"))

    return {
        apiHost: injected.apiHost ?? (localDev ? `ws://${hostname}:${LOCAL_SERVER_PORT}/` : PRODUCTION_API_HOST),
        // A browser build is served beside its own assets; only the shell has to reach out.
        assetBase: injected.assetBase ?? (native ? PRODUCTION_ORIGIN : ""),
        isNative: native,
        // There is no name gate in the shell -- nobody ships a dev prompt -- and no url to put
        // `?guest=true` in either, so the shell always enters as a guest.
        autoGuest: injected.autoGuest ?? native,
    }
}

/** True inside the Capacitor shell. */
export const isNative = (): boolean => resolveConfig().isNative
